import { useEffect, useMemo, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { IconDeviceFloppy, IconLanguage, IconPlus } from "@tabler/icons-react";
import { getIn, useFormik } from "formik";
import * as yup from "yup";
import "quill/dist/quill.snow.css";
import InlineError from "@components/forms/InlineError";
import MediaPicker from "@components/media/MediaPicker";
import AppModal from "@components/modals/AppModal";
import ThinkingAnimation from "@utils/ui/ThinkingAnimation";
import ConfirmDeleteModal from "@components/modals/ConfirmDeleteModal";
import SortableCarbonItem from "@components/home_content/sections/carbon/SortableCarbonItem";
import useLanguages from "@hooks/languages/useLanguages";
import {
  deleteHomeContentCarbonFootPrintRequest,
  listHomeContentCarbonFootPrintRequest,
  saveHomeContentCarbonFootPrintRequest,
} from "@services/homeContent/homeContentCarbonFootPrintApi";
import { createMultimediaRequest } from "@services/multimedia/multimediaApi";
import { translateGenericStream } from "@services/ai/aiApi";
import { inferMediaType, resolveMediaUrl } from "@utils/media/mediaUrl";
import {
  createQuillEditor,
  destroyQuillEditor,
  getQuillHtml,
} from "@utils/quill/editor";
import "@assets/css/mode_tool/translatePointsModal.css";

function buildLanguageList(languages = []) {
  return languages
    .map((language) => ({
      id: language.lng_id || language.id,
      iso: (language.lng_iso || language.iso || "").toLowerCase(),
      name: language.lng_name || language.name || "",
    }))
    .filter((language) => language.iso);
}

function createClientId() {
  return `carbon-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function createEmptyLocalizedMessage(languageList = []) {
  return languageList.reduce((acc, language) => {
    acc[language.iso] = {
      id: "",
      message: "",
    };
    return acc;
  }, {});
}

function createEmptyCarbonItem(languageList = [], order = 1) {
  return {
    clientId: createClientId(),
    id: "",
    order,
    title: createEmptyLocalizedMessage(languageList),
    description: createEmptyLocalizedMessage(languageList),
    image_url: "",
    image: null,
  };
}

function normalizeLocalizedMessage(value, languageList = []) {
  return languageList.reduce((acc, language) => {
    const localizedValue = value?.[language.iso];
    if (
      localizedValue &&
      typeof localizedValue === "object" &&
      !Array.isArray(localizedValue)
    ) {
      acc[language.iso] = {
        id: localizedValue.id || "",
        message: localizedValue.message || "",
      };
      return acc;
    }
    acc[language.iso] = {
      id: "",
      message: typeof localizedValue === "string" ? localizedValue : "",
    };
    return acc;
  }, {});
}

function buildMediaFromImageUrl(imageUrl = "") {
  const resolvedUrl = resolveMediaUrl(imageUrl || "");
  if (!resolvedUrl) return null;
  return {
    id: "",
    sourceId: "",
    url: resolvedUrl,
    mediaType: inferMediaType(resolvedUrl),
    mimeType: "",
  };
}

function normalizeCarbonItems(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    clientId: createClientId(),
    id: item?.id || "",
    order: Number(item?.order) || index + 1,
    title: normalizeLocalizedMessage(item?.title, languageList),
    description: normalizeLocalizedMessage(item?.description, languageList),
    image_url: item?.image_url || "",
    image: buildMediaFromImageUrl(item?.image_url || ""),
  }));
}

function buildInitialValues(languageList = [], items = []) {
  return {
    items: normalizeCarbonItems(items, languageList),
  };
}

function buildValidationSchema(languageList = []) {
  const requiredLanguageIso = languageList.some(
    (language) => language.iso === "en",
  )
    ? "en"
    : languageList[0]?.iso;
  const localizedShape = languageList.reduce((acc, language) => {
    const messageSchema = yup.string().trim();
    acc[language.iso] = yup.object({
      id: yup.string().default(""),
      message:
        language.iso === requiredLanguageIso
          ? messageSchema.required(`${language.iso.toUpperCase()} is required.`)
          : messageSchema,
    });
    return acc;
  }, {});

  return yup.object({
    items: yup.array().of(
      yup.object({
        title: yup.object().shape(localizedShape),
        description: yup.object().shape(localizedShape),
        image_url: yup.string().trim().required("Image is required."),
      }),
    ),
  });
}

function buildTouchedValues(items = [], languageList = []) {
  return {
    items: items.map(() => ({
      title: languageList.reduce((acc, language) => {
        acc[language.iso] = { message: true };
        return acc;
      }, {}),
      description: languageList.reduce((acc, language) => {
        acc[language.iso] = { message: true };
        return acc;
      }, {}),
      image_url: true,
    })),
  };
}

function buildCarbonPayload(items = [], languageList = []) {
  return (Array.isArray(items) ? items : []).map((item, index) => ({
    id: item?.id || "",
    title: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.title?.[language.iso]?.id || "",
        message: item?.title?.[language.iso]?.message || "",
      };
      return acc;
    }, {}),
    description: languageList.reduce((acc, language) => {
      acc[language.iso] = {
        id: item?.description?.[language.iso]?.id || "",
        message: item?.description?.[language.iso]?.message || "",
      };
      return acc;
    }, {}),
    image_url: item?.image_url || "",
    order: index + 1,
  }));
}

function CarbonFootPrintSection() {
  const { languages, loading, error } = useLanguages();
  const languageList = useMemo(() => buildLanguageList(languages), [languages]);
  const [carbonItems, setCarbonItems] = useState([]);
  const [activeLanguageIso, setActiveLanguageIso] = useState("");
  const [expandedItemId, setExpandedItemId] = useState("");
  const [deleteTargetItem, setDeleteTargetItem] = useState(null);
  const [fetchingCarbonItems, setFetchingCarbonItems] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState("");
  const [isTranslateModalOpen, setIsTranslateModalOpen] = useState(false);
  const [translateItemClientId, setTranslateItemClientId] = useState(null);
  const [translateLanguage, setTranslateLanguage] = useState("");
  const [isTranslating, setIsTranslating] = useState(false);
  const [translateError, setTranslateError] = useState("");
  const [translateComplete, setTranslateComplete] = useState(false);
  const [translateProgress, setTranslateProgress] = useState({
    points: [],
    total: 0,
    completed: 0,
    done: false,
  });
  const [uploadingImageById, setUploadingImageById] = useState({});
  const [imageErrorById, setImageErrorById] = useState({});
  const [editor, setEditor] = useState({
    isOpen: false,
    itemIndex: -1,
    languageIso: "",
  });
  const [descriptionHtml, setDescriptionHtml] = useState("");
  const [isEditorReady, setIsEditorReady] = useState(false);
  const [quillHost, setQuillHost] = useState(null);
  const quillInstanceRef = useRef(null);
  const quillHandlerRef = useRef(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const currentLanguageIso = languageList.some(
    (language) => language.iso === activeLanguageIso,
  )
    ? activeLanguageIso
    : languageList[0]?.iso || "";
  const initialValues = useMemo(
    () => buildInitialValues(languageList, carbonItems),
    [carbonItems, languageList],
  );
  const validationSchema = useMemo(
    () => buildValidationSchema(languageList),
    [languageList],
  );
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: async (values) => {
      setSubmitting(true);
      setLocalError("");
      try {
        const response = await saveHomeContentCarbonFootPrintRequest(
          buildCarbonPayload(values?.items || [], languageList),
        );
        setCarbonItems(Array.isArray(response?.body) ? response.body : []);
      } catch (submitError) {
        setLocalError(
          submitError?.message || "Could not save carbon foot print.",
        );
        throw submitError;
      } finally {
        setSubmitting(false);
      }
    },
  });
  const { validateForm } = formik;

  const translationLanguages = useMemo(
    () => languageList.filter((language) => language.iso !== "en"),
    [languageList],
  );
  const translationPercent = translateProgress.total
    ? Math.min(
        100,
        Math.round((translateProgress.completed / translateProgress.total) * 100),
      )
    : 0;
  const translationCounts = translateProgress.points.reduce((counts, point) => {
    const status = String(point?.status || "pending");
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});
  const translateTargetItem = translateItemClientId === null
    ? null
    : (formik.values?.items || []).find(
        (item) => item.clientId === translateItemClientId,
      ) || null;
  const translateTargetTitle = translateTargetItem?.title?.en?.message || "";
  const hasTranslateTarget = translateItemClientId === null
    ? (formik.values?.items || []).length > 0
    : Boolean(translateTargetItem);

  useEffect(() => {
    let isMounted = true;
    async function fetchCarbonItems() {
      setFetchingCarbonItems(true);
      setLocalError("");
      try {
        const response = await listHomeContentCarbonFootPrintRequest();
        if (!isMounted) return;
        setCarbonItems(Array.isArray(response?.body) ? response.body : []);
      } catch (fetchError) {
        if (!isMounted) return;
        setLocalError(
          fetchError?.message || "Could not load carbon foot print.",
        );
      } finally {
        if (isMounted) setFetchingCarbonItems(false);
      }
    }
    fetchCarbonItems();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!languageList.length) return;
    const firstItemId = initialValues?.items?.[0]?.clientId || "";
    if (firstItemId) setExpandedItemId((current) => current || firstItemId);
  }, [initialValues, languageList]);

  useEffect(() => {
    if (fetchingCarbonItems || !languageList.length) return;
    validateForm(initialValues);
  }, [fetchingCarbonItems, initialValues, languageList.length, validateForm]);

  function getFieldError(path) {
    const touched = getIn(formik.touched, path);
    const fieldError = getIn(formik.errors, path);
    return touched ? fieldError || "" : "";
  }

  function updateLocalizedField(index, fieldName, languageIso, nextValue) {
    formik.setFieldValue(
      `items.${index}.${fieldName}.${languageIso}.message`,
      nextValue,
      true,
    );
  }

  function openTranslateModal(itemClientId = null) {
    setTranslateItemClientId(itemClientId);
    setTranslateLanguage("");
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({ points: [], total: 0, completed: 0, done: false });
    setIsTranslateModalOpen(true);
  }

  async function handleTranslateCarbonItems() {
    const items = formik.values?.items || [];
    const itemsToTranslate = translateItemClientId === null
      ? items
      : items.filter((item) => item.clientId === translateItemClientId);
    if (!translateLanguage || !itemsToTranslate.length) return;

    const source = itemsToTranslate.map((item) => ({
      id: String(item.id || item.clientId),
      title: {
        id: String(item?.title?.en?.id || ""),
        message: String(item?.title?.en?.message || ""),
      },
      description: {
        id: String(item?.description?.en?.id || ""),
        message: String(item?.description?.en?.message || ""),
      },
    }));
    setIsTranslating(true);
    setTranslateError("");
    setTranslateComplete(false);
    setTranslateProgress({
      points: source.map((record, index) => ({
        id: record.id,
        label:
          itemsToTranslate[index]?.title?.en?.message || `Carbon entry ${index + 1}`,
        status: "pending",
      })),
      total: source.length,
      completed: 0,
      done: false,
    });

    try {
      const pointStatuses = new Map();
      const pointErrors = new Map();
      const response = await translateGenericStream(
        { language: translateLanguage, data: source },
        ({ event, data }) => {
          if (event === "start") {
            setTranslateProgress((current) => ({
              ...current,
              total: Number(data?.total) || current.total,
            }));
            return;
          }
          if (event !== "point" || !data?.id) return;
          const id = String(data.id);
          const failed = data.status === "error";
          pointStatuses.set(id, failed ? "error" : "completed");
          if (failed) pointErrors.set(id, data?.message || "Translation failed.");
          setTranslateProgress((current) => {
            const points = current.points.map((point) =>
              point.id === id
                ? {
                    ...point,
                    status: failed ? "error" : "completed",
                    message: failed ? data?.message || "Translation failed." : "",
                    translatedName:
                      data?.data?.title?.message || data?.data?.title || "",
                  }
                : point,
            );
            return {
              ...current,
              points,
              completed: points.filter(
                (point) => point.status === "completed" || point.status === "error",
              ).length,
            };
          });
        },
      );
      const translated = Array.isArray(response?.body?.data)
        ? response.body.data
        : [];
      if (!translated.length) {
        throw new Error(
          response?.message || "The translation response did not include carbon entries.",
        );
      }
      const translatedById = new Map(
        translated.map((item) => [String(item?.id || ""), item]),
      );
      formik.setValues((currentValues) => ({
        ...currentValues,
        items: (currentValues?.items || []).map((item) => {
          const requestId = String(item.id || item.clientId);
          if (pointStatuses.get(requestId) === "error") return item;
          const translatedItem = translatedById.get(requestId);
          if (!translatedItem) return item;
          const getTranslatedMessage = (field) => {
            const value = translatedItem?.[field];
            return value && typeof value === "object" ? value.message : value;
          };
          return {
            ...item,
            title: {
              ...(item.title || {}),
              [translateLanguage]: {
                ...(item?.title?.[translateLanguage] || { id: "" }),
                message: String(
                  getTranslatedMessage("title") ?? item?.title?.en?.message ?? "",
                ),
              },
            },
            description: {
              ...(item.description || {}),
              [translateLanguage]: {
                ...(item?.description?.[translateLanguage] || { id: "" }),
                message: String(
                  getTranslatedMessage("description") ??
                    item?.description?.en?.message ??
                    "",
                ),
              },
            },
          };
        }),
      }), true);
      setTranslateComplete(true);
      setTranslateProgress((current) => ({ ...current, done: true }));
      if (pointErrors.size) {
        setTranslateError(
          `${pointErrors.size} carbon entr${pointErrors.size === 1 ? "y" : "ies"} could not be translated. Successful entries were added to the draft.`,
        );
      } else if (response?.error) {
        setTranslateError(response?.message || "Some carbon entries could not be translated.");
      }
    } catch (translationRequestError) {
      setTranslateProgress((current) => ({ ...current, done: true }));
      setTranslateError(
        translationRequestError?.message || "Could not translate carbon entries.",
      );
    } finally {
      setIsTranslating(false);
    }
  }

  function openDescriptionEditor(itemIndex, languageIso, value = "") {
    setEditor({
      isOpen: true,
      itemIndex,
      languageIso,
    });
    setDescriptionHtml(value || "");
  }

  function closeDescriptionEditor() {
    setEditor({
      isOpen: false,
      itemIndex: -1,
      languageIso: "",
    });
    setIsEditorReady(false);
  }

  function saveDescriptionEditor() {
    if (editor.itemIndex < 0 || !editor.languageIso) return;
    formik.setFieldValue(
      `items.${editor.itemIndex}.description.${editor.languageIso}.message`,
      descriptionHtml,
      true,
    );
    formik.setFieldTouched(
      `items.${editor.itemIndex}.description.${editor.languageIso}.message`,
      true,
      true,
    );
    closeDescriptionEditor();
  }

  function reorderWithOrder(items = []) {
    return items.map((item, index) => ({ ...item, order: index + 1 }));
  }

  function handleAddItem() {
    const nextItems = reorderWithOrder([
      ...(formik.values?.items || []),
      createEmptyCarbonItem(languageList, (formik.values?.items?.length || 0) + 1),
    ]);
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId(nextItems[nextItems.length - 1]?.clientId || "");
  }

  async function handleDeletePersistedItem(item) {
    if (!item?.id) return;
    setSubmitting(true);
    setLocalError("");
    try {
      await deleteHomeContentCarbonFootPrintRequest(item.id);
      const nextItems = reorderWithOrder(
        (formik.values?.items || []).filter(
          (currentItem) => currentItem.clientId !== item.clientId,
        ),
      );
      setCarbonItems(nextItems);
      formik.setFieldValue("items", nextItems, true);
      setExpandedItemId((current) =>
        current === item.clientId ? nextItems[0]?.clientId || "" : current,
      );
    } catch (deleteError) {
      setLocalError(
        deleteError?.message || "Could not delete carbon foot print item.",
      );
    } finally {
      setDeleteTargetItem(null);
      setSubmitting(false);
    }
  }

  function handleRemoveItem(itemClientId) {
    const removedItem = (formik.values?.items || []).find(
      (item) => item.clientId === itemClientId,
    );
    if (!removedItem) return;
    setDeleteTargetItem(removedItem);
  }

  function handleDeleteLocalItem(itemClientId) {
    const nextItems = reorderWithOrder(
      (formik.values?.items || []).filter(
        (item) => item.clientId !== itemClientId,
      ),
    );
    formik.setFieldValue("items", nextItems, true);
    setExpandedItemId((current) =>
      current === itemClientId ? nextItems[0]?.clientId || "" : current,
    );
  }

  async function handleConfirmDelete() {
    if (!deleteTargetItem) return;
    if (deleteTargetItem?.id) {
      await handleDeletePersistedItem(deleteTargetItem);
      return;
    }
    handleDeleteLocalItem(deleteTargetItem.clientId);
    setDeleteTargetItem(null);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    if (!active?.id || !over?.id || active.id === over.id) return;
    const items = [...(formik.values?.items || [])];
    const oldIndex = items.findIndex((item) => item.clientId === active.id);
    const newIndex = items.findIndex((item) => item.clientId === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    formik.setFieldValue(
      "items",
      reorderWithOrder(arrayMove(items, oldIndex, newIndex)),
      true,
    );
  }

  async function handleUploadImage(itemClientId, itemIndex, files = []) {
    const [file] = Array.isArray(files) ? files : [];
    if (!file) return;
    setUploadingImageById((prev) => ({ ...prev, [itemClientId]: true }));
    setImageErrorById((prev) => ({ ...prev, [itemClientId]: "" }));
    try {
      const response = await createMultimediaRequest({ file });
      const body = response?.body || {};
      const rawItem = Array.isArray(body?.multimediaList)
        ? body.multimediaList[0]
        : Array.isArray(body?.multimedia)
          ? body.multimedia[0]
          : body?.multimedia || null;
      if (!rawItem) throw new Error("Could not upload image.");
      const media = {
        id: rawItem?.id || rawItem?.mmd_id || "",
        sourceId: rawItem?.id || rawItem?.mmd_id || "",
        url: resolveMediaUrl(rawItem?.url || rawItem?.mmd_url || ""),
        mediaType:
          rawItem?.mediaType ||
          rawItem?.mmd_media_type ||
          inferMediaType(rawItem?.url || rawItem?.mmd_url || ""),
        mimeType: rawItem?.mimeType || rawItem?.mmd_mime_type || "",
      };
      formik.setFieldValue(`items.${itemIndex}.image`, media, false);
      formik.setFieldValue(
        `items.${itemIndex}.image_url`,
        media.url || media.id || "uploaded",
        true,
      );
    } catch (uploadError) {
      setImageErrorById((prev) => ({
        ...prev,
        [itemClientId]: uploadError?.message || "Could not upload image.",
      }));
      formik.setFieldTouched(`items.${itemIndex}.image_url`, true, true);
    } finally {
      setUploadingImageById((prev) => ({ ...prev, [itemClientId]: false }));
    }
  }

  async function handleSave() {
    formik.setTouched(
      buildTouchedValues(formik.values?.items || [], languageList),
      true,
    );
    try {
      await formik.submitForm();
    } catch (error) {
    }
  }

  useEffect(() => {
    if (!editor.isOpen) return;
    if (!quillHost || quillInstanceRef.current) return;
    const quill = createQuillEditor({
      container: quillHost,
      initialHtml: descriptionHtml,
    });
    if (!quill) return;
    quillHandlerRef.current = () => setDescriptionHtml(getQuillHtml(quill));
    quill.on("text-change", quillHandlerRef.current);
    quillInstanceRef.current = quill;
    setIsEditorReady(true);
  }, [descriptionHtml, editor.isOpen, quillHost]);

  useEffect(() => {
    if (!editor.isOpen && quillInstanceRef.current) {
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
      setIsEditorReady(false);
      setQuillHost(null);
    }
  }, [editor.isOpen, quillHost]);

  useEffect(
    () => () => {
      if (!quillInstanceRef.current) return;
      destroyQuillEditor({
        container: quillHost,
        quill: quillInstanceRef.current,
        textChangeHandler: quillHandlerRef.current,
      });
      quillInstanceRef.current = null;
      quillHandlerRef.current = null;
    },
    [quillHost],
  );

  return (
    <section className="home-content-section-card">
      <div className="home-content-form-head">
        <div>
          <h3>Carbon Foot Print</h3>
          <p>Manage the localized carbon footprint entries with image, title, and description.</p>
        </div>
        {languageList.length ? (
          <div className="app-language-section">
            <div className="app-language-switcher" aria-label="Languages">
              {languageList.map((language) => (
                <button
                  key={language.id || language.iso}
                  type="button"
                  className={`app-language-btn ${currentLanguageIso === language.iso ? "is-active" : ""}`}
                  onClick={() => setActiveLanguageIso(language.iso)}
                  aria-pressed={currentLanguageIso === language.iso}
                  title={language.name || language.iso.toUpperCase()}
                >
                  {language.iso.toUpperCase()}
                </button>
              ))}
              {String(currentLanguageIso).toLowerCase() === "en" ? (
                <button
                  type="button"
                  className="home-content-ai-translate"
                  onClick={() => openTranslateModal()}
                  disabled={!(formik.values?.items || []).length || isTranslating}
                  title="AI Translate Carbon Foot Print entries"
                >
                  <IconLanguage size={16} />
                  <span>AI Translate</span>
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {loading || fetchingCarbonItems ? (
        <p className="form-helper">Loading data...</p>
      ) : null}
      {error ? <p className="message-error">{error}</p> : null}
      {localError ? <p className="message-error">{localError}</p> : null}

      {!loading && !fetchingCarbonItems && languageList.length ? (
        <form
          className="home-content-form tour-add-form"
          onSubmit={formik.handleSubmit}
        >
          <div className="home-content-form-shell">
            <div className="home-content-inline-actions">
              <button
                type="button"
                className="app-icon-btn app-icon-btn--add"
                aria-label="Add carbon item"
                title="Add carbon item"
                onClick={handleAddItem}
              >
                <IconPlus size={16} />
              </button>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={(formik.values?.items || []).map((item) => item.clientId)}
                strategy={verticalListSortingStrategy}
              >
                <div className="home-content-accordion-list">
                  {(formik.values?.items || []).map((item, index) => (
                    <SortableCarbonItem
                      key={item.clientId}
                      item={{
                        ...item,
                        id: item.clientId,
                        title: languageList.reduce((acc, language) => {
                          acc[language.iso] =
                            item?.title?.[language.iso]?.message || "";
                          return acc;
                        }, {}),
                      }}
                      visibleLanguageIso={currentLanguageIso}
                      isOpen={expandedItemId === item.clientId}
                      onTranslate={
                        String(currentLanguageIso).toLowerCase() === "en"
                          ? () => openTranslateModal(item.clientId)
                          : undefined
                      }
                      onToggle={() =>
                        setExpandedItemId((current) =>
                          current === item.clientId ? "" : item.clientId,
                        )
                      }
                      onDelete={() => handleRemoveItem(item.clientId)}
                    >
                      <div className="home-content-form-grid">
                        <div className="tour-add-form-group">
                          <p className="tour-add-form-label">
                            title
                            {getFieldError(
                              `items.${index}.title.${currentLanguageIso}.message`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `items.${index}.title.${currentLanguageIso}.message`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <label className="field-label app-form-field">
                            <input
                              className="field-input"
                              value={
                                item?.title?.[currentLanguageIso]?.message || ""
                              }
                              placeholder={`Write title (${currentLanguageIso})`}
                              onChange={(event) =>
                                updateLocalizedField(
                                  index,
                                  "title",
                                  currentLanguageIso,
                                  event.target.value,
                                )
                              }
                              onBlur={() =>
                                formik.setFieldTouched(
                                  `items.${index}.title.${currentLanguageIso}.message`,
                                  true,
                                  true,
                                )
                              }
                            />
                          </label>
                        </div>

                        <div className="tour-add-form-group">
                          <p className="tour-add-form-label">
                            description
                            {getFieldError(
                              `items.${index}.description.${currentLanguageIso}.message`,
                            ) ? (
                              <InlineError>
                                {getFieldError(
                                  `items.${index}.description.${currentLanguageIso}.message`,
                                )}
                              </InlineError>
                            ) : null}
                          </p>
                          <button
                            type="button"
                            className={`field-label app-form-field field-preview-row ${
                              item?.description?.[currentLanguageIso]?.message
                                ? "filled"
                                : ""
                            }`}
                            onClick={() =>
                              openDescriptionEditor(
                                index,
                                currentLanguageIso,
                                item?.description?.[currentLanguageIso]
                                  ?.message || "",
                              )
                            }
                          >
                            {item?.description?.[currentLanguageIso]?.message ? (
                              <span
                                className="field-preview-value field-preview-value-html"
                                dangerouslySetInnerHTML={{
                                  __html: String(
                                    item?.description?.[currentLanguageIso]
                                      ?.message || "",
                                  ),
                                }}
                              />
                            ) : (
                              <span className="field-preview-value">
                                Complete
                              </span>
                            )}
                          </button>
                        </div>
                      </div>

                      {String(currentLanguageIso).toLowerCase() === "en" ? <MediaPicker
                        title="image (required)"
                        titleError={getFieldError(`items.${index}.image_url`) || ""}
                        items={item?.image ? [item.image] : []}
                        isUploading={Boolean(uploadingImageById[item.clientId])}
                        uploadError={imageErrorById[item.clientId] || ""}
                        accept={{ "image/*": [] }}
                        maxFiles={1}
                        invalidTypeMessage="Image only for this section."
                        onRejected={(message) =>
                          setImageErrorById((prev) => ({
                            ...prev,
                            [item.clientId]: message,
                          }))
                        }
                        onUpload={(files) =>
                          handleUploadImage(item.clientId, index, files)
                        }
                        onRemove={() => {
                          formik.setFieldValue(`items.${index}.image`, null, false);
                          formik.setFieldValue(`items.${index}.image_url`, "", true);
                          formik.setFieldTouched(
                            `items.${index}.image_url`,
                            true,
                            true,
                          );
                        }}
                        uploadLabel="Upload carbon image"
                        uploadHint="Only 1 image is allowed for this entry."
                      /> : null}
                    </SortableCarbonItem>
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>

          <div className="mode-route-tool-steps tour-add-save-row">
            <button
              type="button"
              className={`app-icon-btn app-icon-btn--add mode-route-tool-route-btn ${!formik.isValid || submitting ? "is-disabled" : ""} ${submitting ? "is-loading" : ""}`}
              onClick={handleSave}
              disabled={!formik.isValid || submitting}
              aria-disabled={!formik.isValid || submitting}
              aria-label="Save carbon foot print content"
              title="Save carbon foot print content"
            >
              {submitting ? (
                <span className="mode-route-tool-btn-spinner" />
              ) : (
                <>
                  Save
                  <IconDeviceFloppy size={16} />
                </>
              )}
            </button>
          </div>
        </form>
      ) : null}

      <AppModal
        isOpen={isTranslateModalOpen}
        onClose={() => setIsTranslateModalOpen(false)}
        closeDisabled={isTranslating}
        title={
          translateTargetTitle
            ? `AI Translate · ${translateTargetTitle}`
            : "AI Translate · Carbon Foot Print"
        }
        size="sm"
        className="mode-route-translate-modal"
      >
        <p>
          {translateTargetTitle
            ? "Translate this Carbon Foot Print entry's title and description from English."
            : "Translate the title and description of Carbon Foot Print entries from English."}
        </p>
        <fieldset
          className="mode-route-translate-languages"
          disabled={isTranslating || translateComplete}
        >
          <legend>Target language</legend>
          {translationLanguages.map((language) => (
            <label key={language.iso}>
              <input
                type="radio"
                name="carbon-foot-print-translation-language"
                value={language.iso}
                checked={translateLanguage === language.iso}
                onChange={() => setTranslateLanguage(language.iso)}
              />
              <span>{language.name || language.iso.toUpperCase()}</span>
              <small>{language.iso.toUpperCase()}</small>
            </label>
          ))}
        </fieldset>
        {!hasTranslateTarget ? (
          <p>No Carbon Foot Print entries are available to translate.</p>
        ) : null}
        {!translationLanguages.length ? (
          <p>No translation languages are available for this Tour.</p>
        ) : null}
        {isTranslating ? <ThinkingAnimation label="Translating Carbon Foot Print..." /> : null}
        {translateProgress.points.length ? (
          <section
            className={`mode-route-ai-stream ${isTranslating ? "is-running" : "is-finished"}`}
            aria-live="polite"
          >
            <div className="mode-route-ai-stream-heading">
              <strong>
                {translateProgress.done
                  ? "Translation summary"
                  : `Translating ${translateTargetTitle || "Carbon Foot Print"}`}
              </strong>
              <span>
                {translateProgress.completed} / {translateProgress.total}{" "}
                {translateItemClientId === null ? "entries" : "entry"}
              </span>
            </div>
            <div
              className="mode-route-ai-stream-track"
              role="progressbar"
              aria-label="Carbon Foot Print translation progress"
              aria-valuemin={0}
              aria-valuemax={translateProgress.total}
              aria-valuenow={Math.min(
                translateProgress.completed,
                translateProgress.total,
              )}
            >
              <span style={{ width: `${translationPercent}%` }} />
            </div>
            <div className="mode-route-ai-stream-counts">
              <span className="is-completed">{translationCounts.completed || 0} translated</span>
              <span className="is-error">{translationCounts.error || 0} errors</span>
              <span>{translationCounts.pending || 0} waiting</span>
            </div>
            <ol className="mode-route-ai-stream-list">
              {translateProgress.points.map((point, index) => {
                const status = String(point?.status || "pending");
                const statusLabels = {
                  completed: "Translated",
                  error: "Error",
                  pending: "Waiting",
                };
                const statusIcons = { completed: "✓", error: "!", pending: "…" };
                return (
                  <li className={`is-${status}`} key={point.id}>
                    <span className="mode-route-ai-stream-status" aria-hidden="true">
                      {statusIcons[status] || "•"}
                    </span>
                    <span className="mode-route-ai-stream-point">
                      <strong>#{index + 1} · {point.label}</strong>
                      {point.translatedName ? <small>{point.translatedName}</small> : null}
                      {point.message ? <small>{point.message}</small> : null}
                    </span>
                    <span className="mode-route-ai-stream-label">
                      {statusLabels[status] || status}
                    </span>
                  </li>
                );
              })}
            </ol>
          </section>
        ) : null}
        {translateComplete ? (
          <p className="mode-route-translate-success" role="status">
            Translation complete. Review the selected language before saving.
          </p>
        ) : null}
        {translateError ? <p className="message-error" role="alert">{translateError}</p> : null}
        <div className="tour-add-editor-actions">
          <button
            type="button"
            className="button-ghost"
            onClick={() => setIsTranslateModalOpen(false)}
            disabled={isTranslating}
          >
            {translateProgress.done ? "Close" : "Cancel"}
          </button>
          {!translateComplete ? (
            <button
              type="button"
              className="button-primary"
              onClick={handleTranslateCarbonItems}
              disabled={
                !hasTranslateTarget ||
                !translateLanguage ||
                isTranslating ||
                !translationLanguages.length
              }
            >
              {isTranslating ? "Translating..." : "Translate"}
            </button>
          ) : null}
        </div>
      </AppModal>

      <AppModal
        isOpen={editor.isOpen}
        onClose={closeDescriptionEditor}
        title={`description (${editor.languageIso})`}
        size="lg"
      >
        <div className="tour-add-editor">
          {!isEditorReady ? (
            <p className="tour-add-editor-loading">Loading editor...</p>
          ) : null}
          <div ref={setQuillHost} className="tour-add-quill" />
          <div className="tour-add-editor-actions">
            <button
              type="button"
              className="button-primary"
              onClick={saveDescriptionEditor}
            >
              Save
            </button>
            <button
              type="button"
              className="button-ghost"
              onClick={closeDescriptionEditor}
            >
              Cancel
            </button>
          </div>
        </div>
      </AppModal>

      <ConfirmDeleteModal
        isOpen={Boolean(deleteTargetItem)}
        onClose={() => {
          if (submitting) return;
          setDeleteTargetItem(null);
        }}
        onConfirm={handleConfirmDelete}
        submitting={submitting}
        title="Delete carbon foot print item"
        description={
          <>
            Are you sure you want to delete{" "}
            <span>{deleteTargetItem?.title?.en?.message || ""}</span>{" "}
            carbon foot print item?
          </>
        }
      />
    </section>
  );
}

export default CarbonFootPrintSection;
